import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import api from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { contractStatus } from '@/lib/contract';
import { formatDateNL } from '@/lib/format';
import { VerlengDialog } from '@/components/VerlengDialog';
import { BulkVerlengDialog } from '@/components/BulkVerlengDialog';
import {
  FileText, ChevronRight, ChevronLeft, Search, Check, Loader2,
  AlertTriangle, Sparkles, Save, Download, User as UserIcon, Briefcase, Mail, CalendarClock,
} from 'lucide-react';

// XSS protection: only allow inline formatting that our renderer emits.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['h1', 'h2', 'h3', 'p', 'ul', 'li', 'strong', 'em', 'b', 'i', 'hr', 'br'],
  ALLOWED_ATTR: [],
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'a'],
  FORBID_ATTR: ['onload', 'onerror', 'onclick', 'onmouseover', 'onfocus', 'style'],
};

const CATEGORY_META = {
  arbeidsovereenkomst: { label: 'Arbeidsovereenkomst', color: 'bg-blue-50 border-blue-200 text-blue-800', icon: Briefcase },
  addendum: { label: 'Addendum / Aanvulling', color: 'bg-amber-50 border-amber-200 text-amber-800', icon: FileText },
  brief: { label: 'Brief / HR', color: 'bg-emerald-50 border-emerald-200 text-emerald-800', icon: Mail },
};

const STEPS = [
  { id: 1, label: 'Template' },
  { id: 2, label: 'Medewerker' },
  { id: 3, label: 'Gegevens' },
  { id: 4, label: 'Preview & Genereren' },
];

export default function ContractWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { user } = useAuth();
  const isOwnerAdmin = user?.role === 'owner' || user?.role === 'admin';
  const [view, setView] = useState('wizard'); // 'wizard' | 'einddatums'
  const presetEmployeeId = searchParams.get('employee_id');
  const presetTemplateId = searchParams.get('template_id');

  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedTemplateId, setSelectedTemplateId] = useState(presetTemplateId || '');
  const [templateDetail, setTemplateDetail] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [empQuery, setEmpQuery] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [isContractModification, setIsContractModification] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  // -- load templates list
  useEffect(() => {
    api.get('/contracts/templates').then(({ data }) => setTemplates(data.items || []));
  }, []);

  // -- preset employee
  useEffect(() => {
    if (!presetEmployeeId) return;
    api.get(`/employees/${presetEmployeeId}`).then(({ data }) => {
      setSelectedEmployee(data);
      if (presetTemplateId) {
        setStep(3);
      } else {
        setStep(1);
      }
    }).catch(() => {});
  }, [presetEmployeeId, presetTemplateId]);

  // -- load template detail when selected
  useEffect(() => {
    if (!selectedTemplateId) return;
    api.get(`/contracts/templates/${selectedTemplateId}`).then(({ data }) => {
      setTemplateDetail(data);
      // Initialize form values with defaults
      const initial = {};
      data.fields.forEach((f) => {
        if (f.default !== null && f.default !== undefined) initial[f.key] = f.default;
      });
      // Default letter_date = today
      if (data.fields.some((f) => f.key === 'letter_date') && !initial.letter_date) {
        initial.letter_date = new Date().toISOString().slice(0, 10);
      }
      setFormValues(initial);
    });
  }, [selectedTemplateId]);

  // -- employee search
  const searchEmployees = useCallback(async (q) => {
    if (q.length < 2) {
      setEmployees([]);
      return;
    }
    try {
      const { data } = await api.get('/employees', { params: { search: q } });
      setEmployees(data || []);
    } catch {
      setEmployees([]);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchEmployees(empQuery), 250);
    return () => clearTimeout(t);
  }, [empQuery, searchEmployees]);

  // -- filtered templates
  const filteredTemplates = useMemo(() => {
    if (categoryFilter === 'all') return templates;
    return templates.filter((t) => t.category === categoryFilter);
  }, [templates, categoryFilter]);

  // -- navigation
  const canProceed = useMemo(() => {
    if (step === 1) return !!selectedTemplateId;
    if (step === 2) return !!selectedEmployee;
    if (step === 3) {
      if (!templateDetail) return false;
      return templateDetail.fields
        .filter((f) => f.required && f.key !== 'end_date')
        .every((f) => formValues[f.key]);
    }
    return false;
  }, [step, selectedTemplateId, selectedEmployee, templateDetail, formValues]);

  const goNext = async () => {
    if (step === 3) {
      // Generate preview before showing step 4
      await loadPreview();
    }
    setStep((s) => Math.min(4, s + 1));
  };

  const goPrev = () => setStep((s) => Math.max(1, s - 1));

  const loadPreview = async () => {
    if (!selectedTemplateId || !selectedEmployee) return;
    setBusy(true);
    try {
      const { data } = await api.post('/contracts/preview', {
        template_id: selectedTemplateId,
        employee_id: selectedEmployee.id,
        values: formValues,
        is_contract_modification: isContractModification,
      });
      setPreview(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Preview mislukt';
      toast({ title: 'Preview mislukt', description: msg, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/contracts/generate', {
        template_id: selectedTemplateId,
        employee_id: selectedEmployee.id,
        values: formValues,
        is_contract_modification: isContractModification,
      });
      toast({
        title: 'Contract gegenereerd',
        description: `${data.template_title} — opgeslagen in dossier`,
      });
      // Navigate to employee documents tab
      navigate(`/medewerkers/${selectedEmployee.id}?tab=documenten`);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Genereren mislukt';
      toast({ title: 'Genereren mislukt', description: msg, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5 pb-24" data-testid="contract-wizard">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Sparkles size={20} className="text-brand-amber" />
            Contract Wizard
          </h1>
          <p className="text-sm text-slate-500">
            Genereer een contract, addendum of brief in 4 stappen.
          </p>
        </div>
        <Link
          to="/contracten"
          onClick={(e) => {
            e.preventDefault();
            setStep(1);
            setSelectedTemplateId('');
            setSelectedEmployee(null);
            setFormValues({});
            setPreview(null);
          }}
          className="text-sm text-slate-500 hover:text-brand-navy"
        >
          Opnieuw beginnen
        </Link>
      </header>

      {isOwnerAdmin && (
        <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1 w-fit" data-testid="contracten-view-toggle">
          <button
            type="button"
            onClick={() => setView('wizard')}
            data-testid="view-tab-wizard"
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              view === 'wizard' ? 'bg-brand-navy text-white' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            Wizard
          </button>
          <button
            type="button"
            onClick={() => setView('einddatums')}
            data-testid="view-tab-einddatums"
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              view === 'einddatums' ? 'bg-brand-navy text-white' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            Einddatums
          </button>
        </div>
      )}

      {view === 'einddatums' && isOwnerAdmin ? (
        <ContractEinddatumenTab />
      ) : (
      <>
      {/* Stepper */}
      <ol className="flex items-center justify-between gap-1 rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm">
        {STEPS.map((s, idx) => (
          <li key={s.id} className="flex items-center flex-1 min-w-0">
            <div
              className={`flex shrink-0 items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${
                step > s.id
                  ? 'bg-emerald-500 text-white'
                  : step === s.id
                  ? 'bg-brand-navy text-white'
                  : 'bg-slate-200 text-slate-500'
              }`}
              data-testid={`step-indicator-${s.id}`}
            >
              {step > s.id ? <Check size={14} /> : s.id}
            </div>
            <span className={`ml-2 text-xs font-medium truncate ${step === s.id ? 'text-brand-navy' : 'text-slate-500'}`}>
              {s.label}
            </span>
            {idx < STEPS.length - 1 && (
              <div className={`flex-1 mx-2 h-px ${step > s.id ? 'bg-emerald-300' : 'bg-slate-200'}`} />
            )}
          </li>
        ))}
      </ol>

      {/* Step content */}
      {step === 1 && (
        <Step1Template
          templates={filteredTemplates}
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
          selectedTemplateId={selectedTemplateId}
          setSelectedTemplateId={setSelectedTemplateId}
        />
      )}
      {step === 2 && (
        <Step2Employee
          empQuery={empQuery}
          setEmpQuery={setEmpQuery}
          employees={employees}
          selectedEmployee={selectedEmployee}
          setSelectedEmployee={setSelectedEmployee}
        />
      )}
      {step === 3 && (
        <Step3Fields
          templateDetail={templateDetail}
          formValues={formValues}
          setFormValues={setFormValues}
          isContractModification={isContractModification}
          setIsContractModification={setIsContractModification}
        />
      )}
      {step === 4 && (
        <Step4Preview
          preview={preview}
          templateDetail={templateDetail}
          selectedEmployee={selectedEmployee}
          busy={busy}
          onRefresh={loadPreview}
          onGenerate={generate}
        />
      )}

      {/* Navigation buttons */}
      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <button
          onClick={goPrev}
          disabled={step === 1}
          data-testid="wizard-prev"
          className="flex items-center gap-1.5 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={14} /> Vorige
        </button>
        {step < 4 ? (
          <button
            onClick={goNext}
            disabled={!canProceed || busy}
            data-testid="wizard-next"
            className="flex items-center gap-1.5 rounded-md bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : null}
            Volgende <ChevronRight size={14} />
          </button>
        ) : (
          <button
            onClick={generate}
            disabled={busy || !preview}
            data-testid="wizard-generate"
            className="flex items-center gap-1.5 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Genereren & opslaan in dossier
          </button>
        )}
      </div>
      </>
      )}
    </div>
  );
}
// -------------------- Einddatums-overzicht (owner/admin) -----------------

function ContractEinddatumenTab() {
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [verlengEmp, setVerlengEmp] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulk, setShowBulk] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [empResp, locResp] = await Promise.all([
        api.get('/employees', { params: { status: 'actief' } }),
        api.get('/locations'),
      ]);
      setEmployees(empResp.data || []);
      setLocations(locResp.data || []);
    } catch {
      setEmployees([]); setLocations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const locationById = useMemo(() => {
    const m = {};
    locations.forEach((l) => (m[l.id] = l));
    return m;
  }, [locations]);

  const rows = useMemo(() => {
    return employees
      .filter((e) => !!e.contract_einddatum)
      .filter((e) => locationFilter === 'all' || e.home_location_id === locationFilter)
      .sort((a, b) => a.contract_einddatum.localeCompare(b.contract_einddatum));
  }, [employees, locationFilter]);

  const badgeCls = {
    verlopen: 'bg-red-100 text-red-800',
    bijna: 'bg-amber-100 text-amber-800',
    ok: 'bg-slate-100 text-slate-600',
  };
  const badgeLabel = { verlopen: 'Verlopen', bijna: '≤30 dagen', ok: 'Ok' };

  return (
    <div className="space-y-3" data-testid="contract-einddatums-tab">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
          <CalendarClock size={16} /> Contracten op einddatum
          <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600" data-testid="einddatums-count">
            {loading ? '…' : rows.length}
          </span>
        </div>
        <select
          value={locationFilter}
          onChange={(e) => setLocationFilter(e.target.value)}
          data-testid="einddatums-filter-location"
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
        >
          <option value="all">Alle locaties</option>
          {locations.map((l) => (
            <option key={l.id} value={l.id}>{l.name}</option>
          ))}
        </select>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid="einddatums-bulk-bar">
        <label className="flex items-center gap-2 text-slate-700">
          <input
            type="checkbox"
            checked={rows.length > 0 && selectedIds.length === rows.length}
            onChange={(e) => setSelectedIds(e.target.checked ? rows.map((x) => x.id) : [])}
            data-testid="einddatums-select-all"
          />
          Selecteer alles (gefilterd)
        </label>
        <span className="text-slate-500" data-testid="einddatums-selected-count">{selectedIds.length} geselecteerd</span>
        <button
          type="button"
          onClick={() => setShowBulk(true)}
          disabled={selectedIds.length === 0}
          data-testid="btn-einddatums-bulk-verlengen"
          className="rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          Verleng geselecteerden…
        </button>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {loading ? (
          <p className="p-6 text-sm text-slate-500">Laden…</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-slate-500" data-testid="einddatums-empty">Geen actieve medewerkers met een contract-einddatum.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2.5"></th>
                  <th className="px-4 py-2.5 text-left">Naam</th>
                  <th className="px-4 py-2.5 text-left">Locatie</th>
                  <th className="px-4 py-2.5 text-left">Contract t/m</th>
                  <th className="px-4 py-2.5 text-left">Status</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => {
                  const loc = e.home_location_id ? locationById[e.home_location_id] : null;
                  const st = contractStatus(e.contract_einddatum);
                  return (
                    <tr key={e.id} data-testid={`einddatum-row-${e.personeelsnummer}`} className="border-t border-slate-100">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(e.id)}
                          onChange={(ev) => setSelectedIds((prev) => (
                            ev.target.checked ? [...prev, e.id] : prev.filter((x) => x !== e.id)))}
                          data-testid={`einddatums-check-${e.personeelsnummer}`}
                        />
                      </td>
                      <td className="px-4 py-2 font-medium text-slate-900">
                        {e.name} <span className="text-slate-400">#{e.personeelsnummer}</span>
                      </td>
                      <td className="px-4 py-2 text-slate-700">{loc?.name || '—'}</td>
                      <td className="px-4 py-2 tabular-nums">{formatDateNL(e.contract_einddatum)}</td>
                      <td className="px-4 py-2">
                        <span className={`rounded px-2 py-0.5 text-xs ${badgeCls[st] || badgeCls.ok}`} data-testid={`einddatum-badge-${e.personeelsnummer}`}>
                          {badgeLabel[st] || 'Ok'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => setVerlengEmp(e)}
                          data-testid={`einddatum-verleng-${e.personeelsnummer}`}
                          className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                        >
                          Verlengen…
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {verlengEmp && (
        <VerlengDialog employee={verlengEmp} onClose={() => setVerlengEmp(null)} onDone={() => load()} />
      )}
      {showBulk && (
        <BulkVerlengDialog
          employees={rows.filter((e) => selectedIds.includes(e.id))}
          onClose={() => { setShowBulk(false); setSelectedIds([]); load(); }}
          onDone={() => load()}
        />
      )}
    </div>
  );
}



// -------------------- Step 1: Template ---------------------------------

function Step1Template({ templates, categoryFilter, setCategoryFilter, selectedTemplateId, setSelectedTemplateId }) {
  const cats = ['all', 'arbeidsovereenkomst', 'addendum', 'brief'];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {cats.map((c) => (
          <button
            key={c}
            onClick={() => setCategoryFilter(c)}
            data-testid={`cat-filter-${c}`}
            className={`rounded-md px-3 py-1.5 text-sm font-medium border transition-colors ${
              categoryFilter === c
                ? 'bg-brand-navy text-white border-brand-navy'
                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
            }`}
          >
            {c === 'all' ? 'Alle templates' : CATEGORY_META[c]?.label || c}
          </button>
        ))}
      </div>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {templates.map((t) => {
          const meta = CATEGORY_META[t.category] || CATEGORY_META.brief;
          const Icon = meta.icon;
          const active = selectedTemplateId === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setSelectedTemplateId(t.id)}
              data-testid={`template-card-${t.id}`}
              className={`text-left rounded-xl border-2 p-4 transition-all hover:shadow-md ${
                active
                  ? 'border-brand-navy ring-2 ring-brand-navy/20 bg-white'
                  : 'border-slate-200 bg-white hover:border-brand-amber'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className={`flex items-center justify-center w-9 h-9 rounded-md ${meta.color}`}>
                  <Icon size={18} />
                </div>
                {active && <Check size={18} className="text-brand-navy" />}
              </div>
              <h3 className="text-sm font-semibold text-slate-900">{t.title}</h3>
              <p className="text-xs text-slate-500 mt-1">{t.description}</p>
              <div className="flex items-center gap-2 mt-3 text-[10px] text-slate-400">
                <span className={`rounded px-1.5 py-0.5 ${meta.color}`}>{meta.label}</span>
                <span>{t.field_count} velden</span>
                {t.sets_expiry && <span className="text-amber-700">⏰ heeft einddatum</span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// -------------------- Step 2: Employee ---------------------------------

function Step2Employee({ empQuery, setEmpQuery, employees, selectedEmployee, setSelectedEmployee }) {
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <label className="block text-xs font-medium text-slate-600 mb-1">
          Zoek medewerker (naam of personeelsnummer)
        </label>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={empQuery}
            onChange={(e) => setEmpQuery(e.target.value)}
            placeholder="Begin met typen…"
            data-testid="employee-search"
            className="w-full rounded-md border border-slate-300 pl-9 pr-3 py-2 text-sm"
            autoFocus
          />
        </div>
        {selectedEmployee && (
          <div className="mt-3 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-center gap-2" data-testid="employee-selected">
            <UserIcon size={14} className="text-emerald-700" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-emerald-900">{selectedEmployee.name}</p>
              <p className="text-xs text-emerald-700">
                pnr {selectedEmployee.personeelsnummer}
                {selectedEmployee.functie && ` · ${selectedEmployee.functie}`}
              </p>
            </div>
            <button
              onClick={() => setSelectedEmployee(null)}
              className="text-emerald-700 hover:text-emerald-900 text-xs"
            >
              Wijzigen
            </button>
          </div>
        )}
      </div>

      {!selectedEmployee && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          {employees.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 text-center">
              {empQuery.length < 2 ? 'Type minimaal 2 tekens om te zoeken.' : 'Geen resultaten.'}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {employees.slice(0, 20).map((e) => (
                <li
                  key={e.id}
                  onClick={() => setSelectedEmployee(e)}
                  data-testid={`employee-row-${e.id}`}
                  className="px-4 py-2.5 hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm font-medium text-slate-900">{e.name}</p>
                    <p className="text-xs text-slate-500">pnr {e.personeelsnummer} · {e.status}</p>
                  </div>
                  <ChevronRight size={14} className="text-slate-400" />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// -------------------- Step 3: Fields ----------------------------------

function Step3Fields({ templateDetail, formValues, setFormValues, isContractModification, setIsContractModification }) {
  if (!templateDetail) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
        <Loader2 size={20} className="animate-spin mx-auto mb-2" />
        Template laden…
      </div>
    );
  }

  const onChange = (key, value) => {
    setFormValues((s) => ({ ...s, [key]: value }));
  };

  return (
    <div className="space-y-3">
      {templateDetail.has_contract_modification_block && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isContractModification}
              onChange={(e) => setIsContractModification(e.target.checked)}
              data-testid="checkbox-contract-mod"
              className="mt-0.5"
            />
            <div>
              <span className="text-sm font-medium text-amber-900">
                Dit is een wijziging van bestaand dienstverband (geen verlenging)
              </span>
              <p className="text-xs text-amber-700 mt-0.5">
                Vinkje aan → de zin "Dit is een wijziging van het huidige dienstverband, dus geen verlenging." verschijnt bovenaan.
              </p>
            </div>
          </label>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">
          {templateDetail.title}
        </h3>
        <div className="grid gap-3 md:grid-cols-2">
          {templateDetail.fields.map((f) => (
            <FieldInput
              key={f.key}
              field={f}
              value={formValues[f.key] ?? ''}
              onChange={(v) => onChange(f.key, v)}
            />
          ))}
        </div>
      </div>

      <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">
        ℹ️ Velden zoals naam, adres en locatie worden automatisch uit het medewerker- en
        locatie-profiel ingevuld. Ontbrekende gegevens worden gemarkeerd in de preview.
      </div>
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const baseCls = 'w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-navy/20';
  return (
    <div className={field.kind === 'textarea' ? 'md:col-span-2' : ''}>
      <label className="block text-xs font-medium text-slate-700 mb-1">
        {field.label}
        {field.required && <span className="text-rose-600 ml-0.5">*</span>}
      </label>
      {field.kind === 'select' || field.kind === 'radio' ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`field-${field.key}`}
          className={baseCls}
        >
          <option value="">— Kies —</option>
          {(field.options || []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : field.kind === 'textarea' ? (
        <textarea
          rows={3}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          data-testid={`field-${field.key}`}
          className={baseCls}
        />
      ) : (
        <input
          type={field.kind === 'date' ? 'date' : field.kind === 'number' ? 'number' : 'text'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          data-testid={`field-${field.key}`}
          className={baseCls}
          step={field.kind === 'number' ? '0.01' : undefined}
        />
      )}
      {field.help_text && (
        <p className="mt-0.5 text-[10px] text-slate-500">{field.help_text}</p>
      )}
    </div>
  );
}

// -------------------- Step 4: Preview ----------------------------------

function Step4Preview({ preview, templateDetail, selectedEmployee, busy, onRefresh, onGenerate }) {
  if (busy && !preview) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
        <Loader2 size={20} className="animate-spin mx-auto mb-2" />
        Preview wordt gegenereerd…
      </div>
    );
  }
  if (!preview) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
        Kon preview niet laden. Klik op "Vernieuwen" om opnieuw te proberen.
        <button
          onClick={onRefresh}
          className="block mx-auto mt-3 rounded-md border border-slate-300 px-3 py-1.5 text-xs hover:bg-slate-50"
        >
          Vernieuwen
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {preview.warnings?.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="preview-warnings">
          <h4 className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
            <AlertTriangle size={14} /> Aandachtspunten
          </h4>
          <ul className="mt-2 list-disc list-inside text-xs text-amber-800 space-y-1">
            {preview.warnings.map((w, i) => <li key={`warn-${i}-${w.slice(0, 30)}`}>{w}</li>)}
          </ul>
          <p className="text-[11px] text-amber-700 mt-2">
            💡 Ontbrekende werknemer-/locatie-gegevens kun je aanvullen in het medewerker- of locatieprofiel.
            Het contract kan ook nu al gegenereerd worden; de placeholders blijven dan zichtbaar als [??veld??].
          </p>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
          <div className="text-xs text-slate-500">
            Preview: <strong>{templateDetail?.title}</strong> · {selectedEmployee?.name}
          </div>
          <button
            onClick={onRefresh}
            disabled={busy}
            data-testid="preview-refresh"
            className="text-xs text-slate-600 hover:text-brand-navy"
          >
            {busy ? '…' : '⟳ vernieuwen'}
          </button>
        </div>
        <div
          className="prose prose-sm max-w-none p-6 max-h-[60vh] overflow-y-auto"
          data-testid="preview-html"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.rendered_html, SANITIZE_CONFIG) }}
        />
      </div>
    </div>
  );
}
